import React, { useContext, useState } from "react";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";

import { ProductContext } from "./productContext/context";
import { ActionType } from "./productContext/actions";
import { InputOption } from "./productContext/state";
import GroupOptionsSearch from "./GroupOptionsSearch";
import { lang } from "../../../../middleware/lang";

const OptionGroups: React.FC = () => {
  const { dispatch, state } = useContext(ProductContext);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(
    state.option_groups.length > 0 ? 0 : null
  );

  const addGroup = () => {
    dispatch({
      type: ActionType.AddOptionGroup,
      payload: {
        name: "",
        name_en: "",
        min_selections: 0,
        max_selections: 1,
        sort_order: state.option_groups.length,
        options: [],
      },
    });
    setExpandedGroup(state.option_groups.length);
  };

  const migrateToGroups = () => {
    dispatch({ type: ActionType.MigrateOptionsToGroups });
    setExpandedGroup(0);
  };

  // Show migrate button if there are legacy flat options but no groups
  if (state.options.length > 0 && state.option_groups.length === 0) {
    return (
      <div className="material_card p-3 mb-3">
        <h4>{lang("Grupos de Opciones", "Option Groups")}</h4>
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle"></i>{" "}
          {lang(
            "Este producto tiene opciones sin agrupar. Migra a grupos para mayor control (mínimo/máximo por grupo).",
            "This product has ungrouped options. Migrate to groups for more control (min/max per group)."
          )}
        </div>
        <button className="btn btn-primary" onClick={migrateToGroups}>
          <i className="fas fa-layer-group mr-1"></i>
          {lang("Migrar a Grupos", "Migrate to Groups")}
        </button>
      </div>
    );
  }

  return (
    <div className="material_card p-3 mb-3">
      <h4>{lang("Grupos de Opciones", "Option Groups")}</h4>
      <small className="form-text text-muted mb-3">
        {lang(
          "Crea grupos de opciones como tipo de pan, proteína, toppings, salsas, etc.",
          "Create option groups like bread type, protein, toppings, sauces, etc."
        )}
      </small>

      {state.option_groups.map((group, groupIdx) => (
        <div
          key={`group-${groupIdx}`}
          className="border rounded mb-3"
        >
          {/* Group header — click to expand/collapse */}
          <div
            className="d-flex align-items-center justify-content-between p-3 bg-light hover_pointer"
            onClick={() =>
              setExpandedGroup(expandedGroup === groupIdx ? null : groupIdx)
            }
          >
            <div className="d-flex align-items-center">
              <i
                className={`fas fa-chevron-${
                  expandedGroup === groupIdx ? "down" : "right"
                } mr-2 text-muted`}
              ></i>
              <strong>
                {group.name || lang("(Sin nombre)", "(No name)")}
              </strong>
              <span className="badge badge-secondary ml-2">
                {group.options.length} {lang("opciones", "options")}
              </span>
              {group.min_selections > 0 && (
                <span className="badge badge-warning ml-1">
                  {lang("Requerido", "Required")}
                </span>
              )}
            </div>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={(e) => {
                e.stopPropagation();
                dispatch({
                  type: ActionType.RemoveOptionGroup,
                  payload: { groupIdx },
                });
              }}
            >
              <i className="fa fa-trash" />
            </button>
          </div>

          {/* Group body — expanded */}
          {expandedGroup === groupIdx && (
            <div className="p-3">
              <div className="row mb-3">
                <div className="col-6">
                  <label>{lang("Nombre (ES)", "Name (ES)")}</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={group.name}
                    onChange={(e) =>
                      dispatch({
                        type: ActionType.UpdateOptionGroup,
                        payload: {
                          groupIdx,
                          field: "name",
                          value: e.target.value,
                        },
                      })
                    }
                    placeholder={lang("Ej: Tipo de pan", "E.g. Bread type")}
                  />
                </div>
                <div className="col-6">
                  <label>{lang("Nombre (EN)", "Name (EN)")}</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={group.name_en}
                    onChange={(e) =>
                      dispatch({
                        type: ActionType.UpdateOptionGroup,
                        payload: {
                          groupIdx,
                          field: "name_en",
                          value: e.target.value,
                        },
                      })
                    }
                    placeholder={lang("Ej: Bread type", "E.g. Bread type")}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-6">
                  <label>
                    {lang("Mínimo a elegir", "Min selections")}:{" "}
                    <strong>{group.min_selections}</strong>
                  </label>
                  <Slider
                    min={0}
                    max={Math.max(group.options.length, 1)}
                    dots={true}
                    onChange={(value: number) => {
                      dispatch({
                        type: ActionType.UpdateOptionGroup,
                        payload: {
                          groupIdx,
                          field: "min_selections",
                          value,
                        },
                      });
                      if (value > group.max_selections) {
                        dispatch({
                          type: ActionType.UpdateOptionGroup,
                          payload: {
                            groupIdx,
                            field: "max_selections",
                            value,
                          },
                        });
                      }
                    }}
                    value={group.min_selections}
                    step={1}
                  />
                  <small className="form-text text-muted">
                    {group.min_selections === 0
                      ? lang("Opcional", "Optional")
                      : lang("Requerido", "Required")}
                  </small>
                </div>
                <div className="col-6">
                  <label>
                    {lang("Máximo a elegir", "Max selections")}:{" "}
                    <strong>{group.max_selections}</strong>
                  </label>
                  <Slider
                    min={Math.max(group.min_selections, 1)}
                    max={Math.max(group.options.length, 1)}
                    dots={true}
                    onChange={(value) =>
                      dispatch({
                        type: ActionType.UpdateOptionGroup,
                        payload: {
                          groupIdx,
                          field: "max_selections",
                          value,
                        },
                      })
                    }
                    value={group.max_selections}
                    step={1}
                  />
                  <small className="form-text text-muted">
                    {group.max_selections === 1
                      ? lang("Solo una opción", "Single choice")
                      : lang(
                          `Hasta ${group.max_selections} opciones`,
                          `Up to ${group.max_selections} options`
                        )}
                  </small>
                </div>
              </div>

              {/* Options within this group */}
              <GroupOptionsSearch groupIdx={groupIdx} />

              {group.options.length > 0 && (
                <table className="table table-sm mt-2">
                  <thead className="thead-light">
                    <tr>
                      <th>{lang("Opción", "Option")}</th>
                      <th>{lang("+ Precio", "+ Price")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.options.map(
                      (option: InputOption, optionIdx: number) => (
                        <tr key={`group-${groupIdx}-opt-${optionIdx}`}>
                          <td>{option.ingredient.name}</td>
                          <td>
                            <div className="input-group input-group-sm">
                              <div className="input-group-prepend">
                                <span className="input-group-text">$</span>
                              </div>
                              <input
                                type="text"
                                className="form-control"
                                value={option.price_mod}
                                onChange={(e) =>
                                  dispatch({
                                    type: ActionType.SetGroupOptionPrice,
                                    payload: {
                                      groupIdx,
                                      optionIdx,
                                      price: +e.target.value,
                                    },
                                  })
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                dispatch({
                                  type: ActionType.RemoveOptionFromGroup,
                                  payload: { groupIdx, optionIdx },
                                })
                              }
                            >
                              <i className="fa fa-times" />
                            </button>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      ))}

      <button className="btn btn-outline-primary" onClick={addGroup}>
        <i className="fas fa-plus mr-1"></i>
        {lang("Agregar Grupo", "Add Group")}
      </button>
    </div>
  );
};

export default OptionGroups;
